import React, { useState, useEffect } from 'react';
import { Key, Save, Trash2, TestTube, Check, X, Loader2, Eye, EyeOff, Settings } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const SettingsAPI = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showKeys, setShowKeys] = useState({});
  const [editMode, setEditMode] = useState({});
  const [testingProvider, setTestingProvider] = useState(null);

  const providers = [
    {
      id: 'openai',
      name: 'OpenAI',
      models: ['gpt-5', 'gpt-4o', 'gpt-4o-mini', 'o1', 'o3-mini'],
      defaultModel: 'gpt-4o',
      description: 'GPT-5, GPT-4o et autres modèles OpenAI',
      keyFormat: 'sk-...'
    },
    {
      id: 'anthropic',
      name: 'Anthropic (Claude)',
      models: ['claude-3-7-sonnet-20250219', 'claude-4-sonnet-20250514', 'claude-3-5-sonnet-20241022'],
      defaultModel: 'claude-3-7-sonnet-20250219',
      description: 'Claude 4 Sonnet, Claude 3.7 et autres modèles Anthropic',
      keyFormat: 'sk-ant-...'
    },
    {
      id: 'gemini',
      name: 'Google Gemini',
      models: ['gemini-2.5-pro', 'gemini-2.5-flash', 'gemini-2.0-flash'],
      defaultModel: 'gemini-2.5-pro',
      description: 'Gemini 2.5 Pro, Flash et autres modèles Google',
      keyFormat: 'AI...'
    }
  ];

  useEffect(() => {
    loadAPIKeys();
  }, []);

  const loadAPIKeys = async () => {
    try {
      setLoading(true);
      const response = await api.get('/settings/api-keys');
      if (response.data.success) {
        setApiKeys(response.data.api_keys);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des clés:', error);
      toast.error('Erreur lors du chargement des configurations');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveKey = async (provider) => {
    const providerData = providers.find(p => p.id === provider);
    const existingKey = apiKeys.find(k => k.provider === provider);

    const apiKeyValue = editMode[provider]?.api_key || existingKey?.api_key || '';
    const modelValue = editMode[provider]?.model || existingKey?.model || providerData.defaultModel;

    if (!apiKeyValue || apiKeyValue.trim() === '') {
      toast.error('Veuillez entrer une clé API valide');
      return;
    }

    try {
      const response = await api.post('/settings/api-keys', {
        provider,
        api_key: apiKeyValue,
        model: modelValue,
        is_active: true,
        description: `Configuration ${providerData.name}`
      });

      if (response.data.success) {
        toast.success('Configuration enregistrée avec succès');
        loadAPIKeys();
        setEditMode(prev => {
          const newEditMode = { ...prev };
          delete newEditMode[provider];
          return newEditMode;
        });
      }
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement:', error);
      const errorMsg = error.response?.data?.detail || 'Erreur lors de l\'enregistrement';
      toast.error(errorMsg);
    }
  };

  const handleTestKey = async (provider) => {
    try {
      setTestingProvider(provider);
      const response = await api.post(`/settings/api-keys/${provider}/test`);

      if (response.data.success) {
        toast.success('Clé API valide ! Test réussi.');
      } else {
        toast.error(response.data.message || 'Test échoué');
      }
    } catch (error) {
      console.error('Erreur lors du test:', error);
      const errorMsg = error.response?.data?.detail || 'Erreur lors du test de la clé';
      toast.error(errorMsg);
    } finally {
      setTestingProvider(null);
    }
  };

  const handleDeleteKey = async (provider) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette configuration ?')) {
      return;
    }

    try {
      const response = await api.delete(`/settings/api-keys/${provider}`);
      if (response.data.success) {
        toast.success('Configuration supprimée');
        loadAPIKeys();
      }
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleShowKey = (provider) => {
    setShowKeys(prev => ({
      ...prev,
      [provider]: !prev[provider]
    }));
  };

  const handleEditChange = (provider, field, value) => {
    setEditMode(prev => ({
      ...prev,
      [provider]: {
        ...prev[provider],
        [field]: value
      }
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl shadow-xl p-8 mb-6 text-white">
          <div className="flex items-center">
            <Settings className="h-10 w-10 mr-4" />
            <div>
              <h1 className="text-3xl font-bold mb-2">Configuration des Clés API</h1>
              <p className="text-blue-100">Gérez vos clés API pour les différents fournisseurs LLM</p>
            </div>
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <p className="text-sm text-blue-800">
            <strong>Note :</strong> Seule la configuration active sera utilisée par le copilote IA. 
            Vous pouvez configurer plusieurs fournisseurs et basculer entre eux.
          </p>
        </div>

        {/* Providers */}
        <div className="space-y-6">
          {providers.map(provider => {
            const existingKey = apiKeys.find(k => k.provider === provider.id);
            const isActive = existingKey?.is_active;
            const isEditing = editMode[provider.id];

            return (
              <div
                key={provider.id}
                className={`bg-white rounded-xl shadow-lg border-2 ${
                  isActive ? 'border-green-500' : 'border-slate-200'
                } overflow-hidden`}
              >
                <div className={`p-6 ${isActive ? 'bg-green-50' : ''}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <Key className="h-5 w-5 text-slate-600 mr-2" />
                        <h3 className="text-xl font-bold text-slate-800">{provider.name}</h3>
                        {isActive && (
                          <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-300">
                            <Check className="h-3 w-3 mr-1" />
                            Active
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-600 mb-2">{provider.description}</p>
                      <p className="text-xs text-slate-500">Format de clé: {provider.keyFormat}</p>
                    </div>

                    {existingKey && (
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleTestKey(provider.id)}
                          disabled={testingProvider === provider.id}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
                          title="Tester la clé"
                        >
                          {testingProvider === provider.id ? (
                            <Loader2 className="h-5 w-5 animate-spin" />
                          ) : (
                            <TestTube className="h-5 w-5" />
                          )}
                        </button>
                        <button
                          onClick={() => handleDeleteKey(provider.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Configuration Form */}
                  <div className="space-y-4">
                    {/* API Key Input */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Clé API
                      </label>
                      <div className="flex space-x-2">
                        <input
                          type={showKeys[provider.id] ? 'text' : 'password'}
                          value={
                            isEditing
                              ? editMode[provider.id]?.api_key || ''
                              : existingKey?.api_key_masked || ''
                          }
                          onChange={(e) => handleEditChange(provider.id, 'api_key', e.target.value)}
                          placeholder={`Entrez votre clé ${provider.name}...`}
                          className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                          onClick={() => toggleShowKey(provider.id)}
                          className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                        >
                          {showKeys[provider.id] ? (
                            <EyeOff className="h-5 w-5 text-slate-600" />
                          ) : (
                            <Eye className="h-5 w-5 text-slate-600" />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Model Selection */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Modèle
                      </label>
                      <select
                        value={
                          isEditing
                            ? editMode[provider.id]?.model || provider.defaultModel
                            : existingKey?.model || provider.defaultModel
                        }
                        onChange={(e) => handleEditChange(provider.id, 'model', e.target.value)}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {provider.models.map(model => (
                          <option key={model} value={model}>{model}</option>
                        ))}
                      </select>
                    </div>

                    {/* Save Button */}
                    <button
                      onClick={() => handleSaveKey(provider.id)}
                      className="w-full bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors flex items-center justify-center space-x-2"
                    >
                      <Save className="h-5 w-5" />
                      <span>{existingKey ? 'Mettre à jour' : 'Enregistrer'}</span>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Help Section */}
        <div className="mt-8 bg-white rounded-xl shadow-lg p-6 border border-slate-200">
          <h3 className="text-lg font-bold text-slate-800 mb-4">Comment obtenir vos clés API ?</h3>
          <div className="space-y-3 text-sm text-slate-600">
            <div>
              <strong>OpenAI:</strong> Rendez-vous sur{' '}
              <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                platform.openai.com/api-keys
              </a>
            </div>
            <div>
              <strong>Anthropic (Claude):</strong> Créez une clé sur{' '}
              <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                console.anthropic.com
              </a>
            </div>
            <div>
              <strong>Google Gemini:</strong> Obtenez une clé depuis{' '}
              <a href="https://makersuite.google.com/app/apikey" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                Google AI Studio
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsAPI;
